import React, { useState } from 'react'
import type { StepData } from '../../../services/onboardingService'
import { useAuth } from '../../auth/AuthContext'
import verificationCodeService, { VerificationCodeType } from '../../../services/verificationCodeService'
import { Modal } from '../../ui/Modal'
import { Input } from '../../ui/Input'
import { Button } from '../../ui/Button'
import toast from 'react-hot-toast'

interface BureauCheckStepProps {
  onComplete?: (stepData: StepData) => Promise<void>
  stepData?: any
  stepKey?: string
}

const BureauCheckStep: React.FC<BureauCheckStepProps> = ({
  onComplete,
  stepData,
}) => {
  const { user } = useAuth()
  const [showVerificationModal, setShowVerificationModal] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [verifyingCode, setVerifyingCode] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const handleComplete = async () => {
    if (!user?.email) {
      toast.error('No se pudo obtener el email del usuario')
      return
    }

    setShowVerificationModal(true)
    await sendVerificationCode()
  }

  const sendVerificationCode = async () => {
    if (!user?.email) return

    setSendingCode(true)
    try {
      const response = await verificationCodeService.createCode({
        type: VerificationCodeType.IDENTITY_VERIFICATION,
        email: user.email,
        metadata: JSON.stringify({
          purpose: 'pf_credit_bureau_authorization',
          context: 'onboarding_personal_consultation'
        })
      })
      
      if (response.success) {
        setCodeSent(true)
        toast.success('Código de verificación enviado a tu correo')
      } else {
        toast.error(response.message || 'Error al enviar el código')
      }
    } catch (error: any) {
      console.error('Error sending verification code:', error)
      toast.error('Error al enviar el código de verificación')
    } finally {
      setSendingCode(false)
    }
  }

  const handleVerificationConfirm = async () => {
    if (!verificationCode.trim()) {
      toast.error('Por favor ingresa el código de verificación')
      return
    }

    if (!user?.email) {
      toast.error('No se pudo obtener el email del usuario')
      return
    }

    setVerifyingCode(true)
    setIsProcessing(true)
    
    try {
      const response = await verificationCodeService.verifyAndAuthorizePF({
        code: verificationCode.trim(),
        email: user.email,
        type: VerificationCodeType.IDENTITY_VERIFICATION,
        reportType: '007'
      })

      if (response.success) {
        setShowVerificationModal(false)
        
        if (response.reportId) {
          toast.success(`Consulta PF completada. ID: ${response.reportId}`)
        }

        if (onComplete) {
          await onComplete({
            completedAt: new Date().toISOString(),
            reportId: response.reportId,
            rfc: response.rfc,
            reportType: response.reportType,
            status: response.status
          })
        }
      } else {
        toast.error(response.message || 'Código de verificación inválido')
      }
    } catch (error: any) {
      console.error('Error verifying code:', error)
      toast.error('Error al procesar la solicitud de Buró de Crédito')
    } finally {
      setVerifyingCode(false)
      setIsProcessing(false)
    }
  }

  const handleModalClose = () => {
    if (!isProcessing) {
      setShowVerificationModal(false)
      setVerificationCode('')
      setCodeSent(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Consulta Buró
        </h2>
        <p className="text-gray-600">
          Verificación en buró de crédito personal
        </p>
      </div>

      {stepData?.status === 'completed' && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-green-600 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="text-green-800 font-medium">
              Consulta buró completada exitosamente
            </span>
          </div>
        </div>
      )}

      <div className="bg-white p-8 rounded-lg border border-gray-200">
        <div className="text-center">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Verificación Buró de Crédito Personal
          </h3>
          <p className="text-gray-600 mb-6">
            Este paso realizará la consulta a tu historial crediticio personal
            para evaluar tu capacidad de pago.
          </p>

          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <p className="text-sm text-gray-700">
              <strong>Tiempo estimado:</strong> 10 minutos
            </p>
          </div>

          <button
            onClick={handleComplete}
            disabled={stepData?.status === 'completed'}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {stepData?.status === 'completed'
              ? 'Completado'
              : 'Realizar Consulta Buró PF'}
          </button>
        </div>
      </div>

      {/* Verification Modal */}
      <Modal
        isOpen={showVerificationModal}
        onClose={handleModalClose}
        size="sm"
        closeOnOverlay={!isProcessing}
      >
        <div className="p-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Confirmación de Código - Consulta PF
          </h2>
          
          {sendingCode ? (
            <div className="flex flex-col items-center justify-center mb-6">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3"></div>
              <p className="text-gray-600">Enviando código de verificación...</p>
            </div>
          ) : codeSent ? (
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Hemos enviado un código a su correo: <strong>{user?.email}</strong>
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Ingrese el código para autorizar la consulta de Buró de Crédito Personal
              </p>
              
              <div className="mb-4">
                <Input
                  placeholder="Ingresa el código (ej: 123456)"
                  value={verificationCode}
                  onChange={e => setVerificationCode(e.target.value)}
                  className="text-center text-lg font-medium"
                  maxLength={10}
                  disabled={verifyingCode}
                />
              </div>
              
              <button
                onClick={sendVerificationCode}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
                disabled={sendingCode}
              >
                ¿No recibiste el código? Reenviar
              </button>
            </div>
          ) : (
            <div className="mb-6">
              <p className="text-red-600 mb-4">
                Error al enviar el código. Por favor, inténtalo de nuevo.
              </p>
              <Button
                variant="secondary"
                onClick={sendVerificationCode}
                className="w-full mb-4"
              >
                Reintentar envío
              </Button>
            </div>
          )}

          <div className="space-y-3">
            <Button
              variant="primary"
              onClick={handleVerificationConfirm}
              className="w-full"
              disabled={!codeSent || !verificationCode.trim() || verifyingCode}
            >
              {verifyingCode ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Procesando consulta...
                </div>
              ) : (
                'Autorizar consulta PF'
              )}
            </Button>
            {!isProcessing && (
              <Button
                variant="secondary"
                onClick={handleModalClose}
                className="w-full"
              >
                Cancelar
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default BureauCheckStep
